import { Ref, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { differenceInCalendarDays, format, formatDistanceToNow, isThisYear, isToday, isYesterday, parseISO } from 'date-fns'
import {
  AlertTriangle,
  ArrowDownLeft,
  ArrowRightLeft,
  ArrowUpRight,
  Bot,
  CalendarClock,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  History,
  Info,
  ListChecks,
  Mail,
  MessageCircle,
  MessageSquare,
  MessagesSquare,
  NotebookPen,
  Pencil,
  Phone,
  PhoneOff,
  Plus,
  Smartphone,
  Sparkles,
  Trash2,
  UserCheck,
  UserRound,
  X,
} from 'lucide-react'
import {
  Board,
  BoardCard,
  BoardColumn,
  CardComment,
  CardEvent,
  CardLog,
  CardNote,
  CardTask,
  COMMENT_MAX_LENGTH,
  LOG_CHANNEL_OPTIONS,
  NO_REPLY_CLOSE_AFTER,
  NOTES_WARNING,
  addCardEvent,
  boardProfile,
  cardTitle,
  createCardComment,
  createCardLog,
  createCardNote,
  createCardTask,
  deleteCardNote,
  deleteCardTask,
  fetchCardComments,
  fetchCardEvents,
  fetchCardLogs,
  fetchCardNotes,
  fetchCardTasks,
  fetchStudentName,
  fieldDef,
  isCardFieldKey,
  isStuck,
  markCardSeen,
  optionLabel,
  setCardTaskDone,
  updateCard,
} from '../../lib/boards'
import { useAuth } from '../../contexts/AuthContext'
import { canUseTutoringPages } from '../../lib/roles'
import { CardFieldInput, TutorOption } from './CardFieldInput'
import { CardEmailsPanel } from './CardEmailsPanel'
import {
  Avatar,
  columnAccent,
  ghostButton,
  iconButton,
  overlayClass,
  panelClass,
  primaryButton,
  sectionTitleClass,
} from './ui'

export type StaffOption = { id: string; name: string }

type Tab = 'details' | 'comments' | 'notes' | 'tasks' | 'log' | 'history'

interface CardModalProps {
  card: BoardCard
  boards: Board[]
  columns: BoardColumn[]
  tutors: TutorOption[]
  staff: StaffOption[]
  actorEmail: string | null
  noAnswerCount: number
  onClose: () => void
  onCardUpdated: (card: BoardCard) => void
  onMoveRequest: (card: BoardCard, target: BoardColumn) => void
  onCloseNoReply: (card: BoardCard) => void
  onDelete: (card: BoardCard) => void
  onCountsChanged: () => void
}

export function CardModal({
  card,
  boards,
  columns,
  tutors,
  staff,
  actorEmail,
  noAnswerCount,
  onClose,
  onCardUpdated,
  onMoveRequest,
  onCloseNoReply,
  onDelete,
  onCountsChanged,
}: CardModalProps) {
  const [tab, setTab] = useState<Tab>(card.hasNewLog && !card.isUnreviewed ? 'log' : 'details')
  const [openedUnreviewedId, setOpenedUnreviewedId] = useState<string | null>(null)
  const seenCardId = useRef<string | null>(null)
  // Each visit needs a comment or a note about the card before the window closes (X or a click
  // outside). One from an earlier visit doesn't count. Deleting the card or moving it to another
  // board still closes it. If comments can't be loaded or saved, it may close without one,
  // rather than trap the user until they reload the page.
  const [commentedCardId, setCommentedCardId] = useState<string | null>(null)
  const [notedCardId, setNotedCardId] = useState<string | null>(null)
  const [commentsUnavailableCardId, setCommentsUnavailableCardId] = useState<string | null>(null)
  const [closeAttempts, setCloseAttempts] = useState(0)
  const [commentDraft, setCommentDraft] = useState('')
  const commented = commentedCardId === card.id
  const noted = notedCardId === card.id
  const commentsUnavailable = commentsUnavailableCardId === card.id
  const canClose = commented || noted || commentsUnavailable
  const markCommentsUnavailable = useCallback(() => setCommentsUnavailableCardId(card.id), [card.id])
  const column = columns.find(c => c.id === card.columnId)
  const boardKey = boards.find(b => b.id === card.boardId)?.key
  const stuck = column ? isStuck(card, column) : false
  const accent = column ? columnAccent(column) : null

  // Opening the card clears its "New" and "New log" badges, once. The board's copy can be a minute
  // old, so the database decides what to clear. A log that arrives while the card is open keeps its badge.
  useEffect(() => {
    if (seenCardId.current === card.id) return
    seenCardId.current = card.id
    if (card.isUnreviewed) setOpenedUnreviewedId(card.id)
    markCardSeen(card.id)
      .then(seen => {
        if (!seen) return
        onCardUpdated(seen.card)
        if (seen.wasUnreviewed) return addCardEvent(card.id, 'updated', 'Marked as reviewed (card opened)', actorEmail)
      })
      .catch(e => console.error('Could not mark the card seen (non-fatal):', e))
  }, [card.id, card.isUnreviewed, actorEmail, onCardUpdated])

  useEffect(() => {
    setCloseAttempts(0)
    setCommentDraft('')
  }, [card.id])

  const requestClose = () => {
    if (canClose) {
      onClose()
      return
    }
    // On the Notes tab a note will do, so it stays there and keeps the half-written note.
    if (tab !== 'notes') setTab('comments')
    setCloseAttempts(n => n + 1)
  }

  const tabs: { key: Tab; label: string; icon: typeof Info }[] = [
    { key: 'details', label: 'Details', icon: Info },
    { key: 'comments', label: 'Comments', icon: MessagesSquare },
    { key: 'notes', label: 'Notes', icon: NotebookPen },
    { key: 'tasks', label: 'Tasks', icon: ListChecks },
    { key: 'log', label: 'Log', icon: MessageSquare },
    { key: 'history', label: 'History', icon: History },
  ]

  return (
    <div className={`${overlayClass} z-50`} onClick={requestClose}>
      <div
        className={`${panelClass} w-full max-w-2xl max-h-[90vh] flex flex-col`}
        role="dialog"
        aria-modal="true"
        aria-label={cardTitle(card)}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white truncate">{cardTitle(card)}</h2>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-md ${accent?.soft ?? 'bg-gray-500/10 text-gray-600 dark:text-gray-300'}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${accent?.dot ?? 'bg-gray-400'}`} />
                  {column?.name ?? 'Unknown column'}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {formatDistanceToNow(parseISO(card.columnEnteredAt))} in this column
                </span>
                {stuck && (
                  <span className="text-xs px-2 py-0.5 rounded-md bg-red-500/10 text-red-600 dark:text-red-400 font-medium flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> Stuck
                  </span>
                )}
                {card.isUnreviewed && (
                  <span className="text-xs px-2 py-0.5 rounded-md bg-amber-500/10 text-amber-600 dark:text-amber-400 font-medium flex items-center gap-1">
                    <Sparkles className="h-3 w-3" /> New, unreviewed
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={requestClose}
              aria-label="Close"
              title={canClose ? 'Close' : 'Add a comment or a note to close this card'}
              className={iconButton}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Scrolls sideways on a phone, where the tabs don't fit. */}
          <div className="mt-5 overflow-x-auto">
            <div className="flex w-max min-w-full gap-1 border-b border-gray-200 dark:border-white/10" role="tablist">
              {tabs.map(t => {
                const Icon = t.icon
                const active = tab === t.key
                return (
                  <button
                    key={t.key}
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTab(t.key)}
                    className={`relative flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap px-3 pt-1 pb-2.5 text-sm font-medium transition-colors ${
                      active ? 'text-gray-900 dark:text-white' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                  >
                    <Icon className={`h-4 w-4 ${active ? 'text-brand-600 dark:text-brand-400' : ''}`} />
                    {t.label}
                    {active && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-brand-400" />}
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        <div className={`px-6 pt-6 overflow-y-auto flex-1 ${tab === 'details' ? '' : 'pb-6'}`}>
          {tab === 'details' && (
            <DetailsTab
              card={card}
              boardKey={boardKey}
              createdAutomatically={openedUnreviewedId === card.id}
              onCountsChanged={onCountsChanged}
              column={column}
              tutors={tutors}
              actorEmail={actorEmail}
              noAnswerCount={noAnswerCount}
              onCardUpdated={onCardUpdated}
              onCloseNoReply={onCloseNoReply}
              onDelete={onDelete}
            />
          )}
          {tab === 'comments' && (
            <CommentsTab
              cardId={card.id}
              actorEmail={actorEmail}
              draft={commentDraft}
              onDraftChange={setCommentDraft}
              commented={commented}
              noted={noted}
              unavailable={commentsUnavailable}
              closeAttempts={closeAttempts}
              onCommented={() => setCommentedCardId(card.id)}
              onUnavailable={markCommentsUnavailable}
            />
          )}
          {tab === 'notes' && (
            <NotesTab
              cardId={card.id}
              actorEmail={actorEmail}
              noted={noted}
              blocked={!canClose && closeAttempts > 0}
              closeAttempts={closeAttempts}
              onNoted={() => setNotedCardId(card.id)}
            />
          )}
          {tab === 'history' && <HistoryTab cardId={card.id} />}
          {tab === 'tasks' && <TasksTab card={card} staff={staff} actorEmail={actorEmail} onChanged={onCountsChanged} />}
          {tab === 'log' && <LogTab card={card} actorEmail={actorEmail} onChanged={onCountsChanged} />}
        </div>

        <MoveBar card={card} boards={boards} columns={columns} onMove={(target) => onMoveRequest(card, target)} />
      </div>
    </div>
  )
}

function StudentRecordLine({ studentId }: { studentId: string | null }) {
  const { profile } = useAuth()
  const [name, setName] = useState<string | null>(null)

  useEffect(() => {
    setName(null)
    if (!studentId) return
    let live = true
    fetchStudentName(studentId)
      .then(n => { if (live) setName(n) })
      .catch(e => console.error('Could not load the student name (non-fatal):', e))
    return () => { live = false }
  }, [studentId])

  if (!studentId) {
    return <p className="-mt-1 mb-3 text-xs text-gray-500 dark:text-gray-400">Saving a student first name creates their student record.</p>
  }

  const label = name ?? 'linked'
  return (
    <p className="-mt-1 mb-3 flex items-start gap-1.5 text-xs text-gray-600 dark:text-gray-300">
      <CheckCircle2 className="h-3.5 w-3.5 mt-px flex-shrink-0 text-emerald-500" />
      <span>
        Student record:{' '}
        {canUseTutoringPages(profile?.role) ? (
          <Link to={`/dashboard/students/${studentId}`} className="font-medium text-brand-600 dark:text-brand-400 hover:underline">
            {label}
          </Link>
        ) : (
          <span className="font-medium">{label}</span>
        )}
        . Name, grade, tutor and family details saved here update it too.
      </span>
    </p>
  )
}

function DetailsTab({
  card,
  boardKey,
  createdAutomatically,
  onCountsChanged,
  column,
  tutors,
  actorEmail,
  noAnswerCount,
  onCardUpdated,
  onCloseNoReply,
  onDelete,
}: {
  card: BoardCard
  boardKey: string | undefined
  createdAutomatically: boolean
  onCountsChanged: () => void
  column: BoardColumn | undefined
  tutors: TutorOption[]
  actorEmail: string | null
  noAnswerCount: number
  onCardUpdated: (card: BoardCard) => void
  onCloseNoReply: (card: BoardCard) => void
  onDelete: (card: BoardCard) => void
}) {
  const [draft, setDraft] = useState<Partial<BoardCard>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  useEffect(() => {
    setDraft({})
    setConfirmDelete(false)
  }, [card.id])

  const merged = { ...card, ...draft }
  const dirty = Object.keys(draft).length > 0

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      const updated = await updateCard(card.id, draft)
      const changed = Object.keys(draft)
        .map(k => (isCardFieldKey(k) ? fieldDef(k, boardKey).label : k))
        .join(', ')
      await addCardEvent(card.id, 'updated', `Updated ${changed}`, actorEmail)
      onCardUpdated(updated)
      setDraft({})
    } catch (e: any) {
      setError(e?.message || 'Could not save.')
    } finally {
      setSaving(false)
    }
  }

  const showNoReplyClose = column?.key === 'no_answer' && noAnswerCount >= NO_REPLY_CLOSE_AFTER

  return (
    <div className="space-y-6">
      {createdAutomatically && (
        <div className="flex items-center gap-3 p-3 rounded-xl border border-amber-500/20 bg-amber-500/10 text-sm text-amber-700 dark:text-amber-300">
          <Sparkles className="h-4 w-4 flex-shrink-0" />
          <span>This card was created automatically. Check the details.</span>
        </div>
      )}

      {showNoReplyClose && (
        <div className="flex flex-wrap items-center gap-3 p-3 rounded-xl border border-red-500/20 bg-red-500/10 text-sm text-red-600 dark:text-red-300">
          <PhoneOff className="h-4 w-4 flex-shrink-0" />
          <span className="flex-1 min-w-[10rem]">{noAnswerCount} tries with no answer.</span>
          <button
            onClick={() => onCloseNoReply(card)}
            className="h-8 px-3 rounded-lg bg-red-500 text-white text-xs font-medium hover:bg-red-600 transition-colors"
          >
            Close: no reply after {NO_REPLY_CLOSE_AFTER} tries
          </button>
        </div>
      )}

      {column?.helpText && (
        <p className="flex items-start gap-2 p-3 rounded-xl bg-gray-50 dark:bg-white/[0.03] text-sm text-gray-600 dark:text-gray-300">
          <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-gray-400" /> {column.helpText}
        </p>
      )}

      <CardEmailsPanel card={card} actorEmail={actorEmail} onCountsChanged={onCountsChanged} />

      {(card.pauseReason || card.closeReason) && (
        <div className="p-3 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.03] text-sm text-gray-700 dark:text-gray-300 space-y-1">
          {card.pauseReason && <p><span className="font-medium">Paused:</span> {card.pauseReason}</p>}
          {card.closeReason && (
            <p>
              <span className="font-medium">Closed:</span> {optionLabel(fieldDef('closeReason', boardKey).options, card.closeReason)}
              {card.closeReasonOther ? ` — ${card.closeReasonOther}` : ''}
            </p>
          )}
        </div>
      )}

      {boardProfile(boardKey).sections.map(section => (
        <div key={section.title}>
          <h3 className={`${sectionTitleClass} mb-3`}>{section.title}</h3>
          {section.fields.includes('studentFirstName') && <StudentRecordLine studentId={card.studentId} />}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-4">
            {section.fields.map(key => {
              const def = fieldDef(key, boardKey)
              return (
                <div key={key} className={def.type === 'checkbox' ? 'sm:col-span-2' : ''}>
                  <CardFieldInput
                    def={def}
                    value={(merged as any)[key]}
                    onChange={(v) => setDraft(prev => ({ ...prev, [key]: v }))}
                    tutors={tutors}
                  />
                </div>
              )
            })}
          </div>
        </div>
      ))}

      {error && <p className="text-sm text-red-500">{error}</p>}

      <div className="sticky bottom-0 -mx-6 px-6 py-3 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 dark:border-white/[0.06] bg-white dark:bg-surface-100">
        {confirmDelete ? (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-red-500">Delete this card and its history?</span>
            <button onClick={() => onDelete(card)} className="h-8 px-3 rounded-lg bg-red-500 text-white text-xs font-medium hover:bg-red-600 transition-colors">
              Delete
            </button>
            <button onClick={() => setConfirmDelete(false)} className="h-8 px-3 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors">
              Keep
            </button>
          </div>
        ) : (
          <button
            onClick={() => setConfirmDelete(true)}
            className="inline-flex items-center gap-1.5 h-8 px-2 -ml-2 rounded-lg text-sm text-gray-500 hover:text-red-500 hover:bg-red-500/10 transition-colors"
          >
            <Trash2 className="h-4 w-4" /> Delete card
          </button>
        )}
        <div className="flex items-center gap-2">
          {dirty && <span className="hidden sm:inline text-xs text-gray-500 dark:text-gray-400">Unsaved changes</span>}
          {dirty && (
            <button onClick={() => setDraft({})} className={ghostButton}>
              Discard
            </button>
          )}
          <button onClick={save} disabled={!dirty || saving} className={primaryButton}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  )
}

const compactSelectClass =
  'h-9 px-2.5 rounded-lg border border-gray-300 dark:border-white/10 bg-white dark:bg-surface-0 text-sm text-gray-900 dark:text-white shadow-sm transition focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-400/25'

function MoveBar({
  card,
  boards,
  columns,
  onMove,
}: {
  card: BoardCard
  boards: Board[]
  columns: BoardColumn[]
  onMove: (target: BoardColumn) => void
}) {
  const [boardId, setBoardId] = useState(card.boardId)
  const [columnId, setColumnId] = useState('')

  useEffect(() => {
    setBoardId(card.boardId)
    setColumnId('')
  }, [card.id, card.boardId, card.columnId])

  const boardColumns = useMemo(
    () => columns.filter(c => c.boardId === boardId).sort((a, b) => a.sortOrder - b.sortOrder),
    [columns, boardId]
  )
  const target = boardColumns.find(c => c.id === columnId)

  return (
    <div className="flex flex-wrap items-center gap-2 px-6 py-3.5 border-t border-gray-100 dark:border-white/[0.06] bg-gray-50/80 dark:bg-white/[0.02] rounded-b-2xl">
      <span className="flex items-center gap-1.5 mr-1 text-sm font-medium text-gray-600 dark:text-gray-300">
        <ArrowRightLeft className="h-4 w-4 text-gray-400" /> Move to
      </span>
      <select
        value={boardId}
        onChange={(e) => {
          setBoardId(e.target.value)
          setColumnId('')
        }}
        aria-label="Board"
        className={compactSelectClass}
      >
        {boards.map(b => (
          <option key={b.id} value={b.id}>
            {b.name}
          </option>
        ))}
      </select>
      <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />
      <select
        value={columnId}
        onChange={(e) => setColumnId(e.target.value)}
        aria-label="Column"
        className={`${compactSelectClass} flex-1 min-w-[11rem]`}
      >
        <option value="" disabled>
          Choose a column…
        </option>
        {boardColumns.map(c => (
          <option key={c.id} value={c.id} disabled={c.id === card.columnId}>
            {c.name}
            {c.id === card.columnId ? ' (current)' : ''}
          </option>
        ))}
      </select>
      <button onClick={() => target && onMove(target)} disabled={!target} className={primaryButton}>
        Move
      </button>
    </div>
  )
}

// Notes and comments are both dated entries with an author: they share the box to write one
// and the list that shows them by day.
type CardEntry = { id: string; createdAt: string; body: string }

function EntryComposer({
  value,
  onChange,
  onSubmit,
  busy,
  label,
  placeholder,
  buttonLabel,
  maxLength,
  inputRef,
}: {
  value: string
  onChange: (text: string) => void
  onSubmit: () => void
  busy: boolean
  label: string
  placeholder: string
  buttonLabel: string
  maxLength?: number
  inputRef?: Ref<HTMLTextAreaElement>
}) {
  return (
    <div className={composerClass}>
      <textarea
        ref={inputRef}
        rows={3}
        value={value}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) onSubmit()
        }}
        placeholder={placeholder}
        aria-label={label}
        className="block w-full resize-y rounded-t-xl bg-transparent px-3.5 pt-3 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none"
      />
      <div className="flex flex-wrap items-center gap-2 px-3 pt-1 pb-3">
        <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3 flex-shrink-0" /> {NOTES_WARNING}
        </p>
        <button
          onClick={onSubmit}
          disabled={busy || !value.trim()}
          className="ml-auto inline-flex items-center h-8 px-3.5 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {busy ? 'Adding…' : buttonLabel}
        </button>
      </div>
    </div>
  )
}

function EntryList<T extends CardEntry>({
  entries,
  authorOf,
  emptyIcon: EmptyIcon,
  emptyTitle,
  emptyHint,
  onDelete,
  deleteLabel,
}: {
  entries: T[]
  authorOf: (entry: T) => string | null
  emptyIcon: typeof Info
  emptyTitle: string
  emptyHint: string
  onDelete?: (entry: T) => void
  deleteLabel?: string
}) {
  const days = useMemo(() => groupByDay(entries), [entries])

  if (entries.length === 0) {
    return (
      <div className="flex flex-col items-center text-center py-10">
        <div className="h-11 w-11 rounded-full bg-brand-400/10 text-brand-600 dark:text-brand-400 flex items-center justify-center mb-3">
          <EmptyIcon className="h-5 w-5" />
        </div>
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{emptyTitle}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{emptyHint}</p>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {days.map(day => (
        <div key={day.key}>
          <h3 className={`${sectionTitleClass} mb-2.5`}>{day.label}</h3>
          <ul className="space-y-2">
            {day.items.map(entry => {
              const author = authorOf(entry)
              return (
                <li key={entry.id} className="group flex gap-3 p-3 rounded-xl border border-gray-200/80 dark:border-white/[0.08]">
                  <Avatar name={author || '?'} size="md" />
                  <div className="flex-1 min-w-0">
                    <p className="flex flex-wrap items-baseline gap-x-2 text-xs text-gray-500 dark:text-gray-400">
                      <span className="font-medium text-gray-700 dark:text-gray-200">{author || 'Unknown'}</span>
                      <time dateTime={entry.createdAt} className="tabular-nums">{format(parseISO(entry.createdAt), 'h:mm a')}</time>
                    </p>
                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap break-words">{entry.body}</p>
                  </div>
                  {onDelete && (
                    <button
                      onClick={() => onDelete(entry)}
                      title={deleteLabel}
                      aria-label={deleteLabel}
                      className="self-start p-1 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-500/10 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 transition"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      ))}
    </div>
  )
}

function CommentsTab({
  cardId,
  actorEmail,
  draft,
  onDraftChange,
  commented,
  noted,
  unavailable,
  closeAttempts,
  onCommented,
  onUnavailable,
}: {
  cardId: string
  actorEmail: string | null
  draft: string
  onDraftChange: (text: string) => void
  commented: boolean
  // A note saved on the Notes tab this visit also lets the card close.
  noted: boolean
  unavailable: boolean
  closeAttempts: number
  onCommented: () => void
  // Comments couldn't be loaded or saved: the card may close without one rather than trap the user.
  onUnavailable: () => void
}) {
  const [comments, setComments] = useState<CardComment[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    setComments(null)
    setError(null)
    let live = true
    fetchCardComments(cardId)
      .then(c => { if (live) setComments(c) })
      .catch(e => {
        if (!live) return
        setError(e?.message || 'Could not load comments.')
        onUnavailable()
      })
    return () => { live = false }
  }, [cardId, onUnavailable])

  // Trying to close the card without a comment lands here, with the cursor in the box.
  useEffect(() => {
    if (closeAttempts > 0) inputRef.current?.focus()
  }, [closeAttempts])

  const blocked = !commented && !noted && !unavailable && closeAttempts > 0

  const add = async () => {
    if (!draft.trim() || adding) return
    setAdding(true)
    setError(null)
    try {
      const comment = await createCardComment(cardId, draft)
      setComments(prev => [comment, ...(prev ?? [])])
      onDraftChange('')
      onCommented()
    } catch (e: any) {
      setError(e?.message || 'Could not add the comment.')
      onUnavailable()
      return
    } finally {
      setAdding(false)
    }
    addCardEvent(cardId, 'updated', 'Added a comment', actorEmail).catch(e =>
      console.error('Could not add the comment to the history (non-fatal):', e)
    )
  }

  return (
    <div className="space-y-5">
      {commented || noted ? (
        <p className="flex items-center gap-2 p-3 rounded-xl border border-green-500/20 bg-green-500/10 text-sm text-green-700 dark:text-green-300">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" /> {commented ? 'Comment' : 'Note'} saved. You can close the card now.
        </p>
      ) : unavailable ? (
        <p role="status" className="flex items-center gap-2 p-3 rounded-xl border border-amber-500/30 bg-amber-500/10 text-sm text-amber-700 dark:text-amber-300">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" /> Comments aren't working right now, so you can close this card without one.
        </p>
      ) : (
        <p
          key={blocked ? 'blocked' : 'hint'}
          role={blocked ? 'alert' : undefined}
          className={`flex items-center gap-2 p-3 rounded-xl border text-sm ${
            blocked
              ? 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300'
              : 'border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.03] text-gray-600 dark:text-gray-300'
          }`}
        >
          <MessagesSquare className="h-4 w-4 flex-shrink-0" />
          {blocked
            ? 'Add a comment or a note about this card before closing it.'
            : 'Before you close this card, add a comment or a note about it.'}
        </p>
      )}

      <EntryComposer
        inputRef={inputRef}
        value={draft}
        onChange={onDraftChange}
        onSubmit={add}
        busy={adding}
        label="New comment"
        placeholder="Write a comment, e.g. Called the parent, waiting for their schedule"
        buttonLabel="Add comment"
        maxLength={COMMENT_MAX_LENGTH}
      />

      {error && <p className="text-sm text-red-500">{error}</p>}

      {!comments ? (
        !error && <Spinner />
      ) : (
        <EntryList
          entries={comments}
          authorOf={c => c.authorEmail}
          emptyIcon={MessagesSquare}
          emptyTitle="No comments yet"
          emptyHint="Each comment is saved with its date and who wrote it."
        />
      )}
    </div>
  )
}

function NotesTab({
  cardId,
  actorEmail,
  noted,
  blocked,
  closeAttempts,
  onNoted,
}: {
  cardId: string
  actorEmail: string | null
  // A note saved this visit lets the card close, like a comment.
  noted: boolean
  // The card was closed with nothing written this visit.
  blocked: boolean
  closeAttempts: number
  onNoted: () => void
}) {
  const [notes, setNotes] = useState<CardNote[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [body, setBody] = useState('')
  const [adding, setAdding] = useState(false)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    setNotes(null)
    fetchCardNotes(cardId).then(setNotes).catch(e => setError(e?.message || 'Could not load notes.'))
  }, [cardId])

  // Trying to close the card from here keeps the cursor in the note box.
  useEffect(() => {
    if (closeAttempts > 0) inputRef.current?.focus()
  }, [closeAttempts])

  const add = async () => {
    if (!body.trim() || adding) return
    setAdding(true)
    setError(null)
    try {
      const note = await createCardNote(cardId, body, actorEmail)
      setNotes(prev => [note, ...(prev ?? [])])
      setBody('')
      onNoted()
    } catch (e: any) {
      setError(e?.message || 'Could not add the note.')
      return
    } finally {
      setAdding(false)
    }
    addCardEvent(cardId, 'updated', 'Added a note', actorEmail).catch(e =>
      console.error('Could not add the note to the history (non-fatal):', e)
    )
  }

  const remove = async (note: CardNote) => {
    try {
      await deleteCardNote(note.id)
      setNotes(prev => (prev ?? []).filter(n => n.id !== note.id))
    } catch (e: any) {
      setError(e?.message || 'Could not delete the note.')
    }
  }

  return (
    <div className="space-y-5">
      {noted ? (
        <p className="flex items-center gap-2 p-3 rounded-xl border border-green-500/20 bg-green-500/10 text-sm text-green-700 dark:text-green-300">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" /> Note saved. You can close the card now.
        </p>
      ) : (
        blocked && (
          <p role="alert" className="flex items-center gap-2 p-3 rounded-xl border border-amber-500/30 bg-amber-500/10 text-sm text-amber-700 dark:text-amber-300">
            <NotebookPen className="h-4 w-4 flex-shrink-0" /> Add a note or a comment about this card before closing it.
          </p>
        )
      )}

      <EntryComposer
        inputRef={inputRef}
        value={body}
        onChange={setBody}
        onSubmit={add}
        busy={adding}
        label="New note"
        placeholder="Add a note, e.g. Prefers Tuesdays after school"
        buttonLabel="Add note"
      />

      {error && <p className="text-sm text-red-500">{error}</p>}

      {!notes ? (
        !error && <Spinner />
      ) : (
        <EntryList
          entries={notes}
          authorOf={n => n.authorName}
          emptyIcon={NotebookPen}
          emptyTitle="No notes yet"
          emptyHint="Each note is saved with its date and who wrote it."
          onDelete={remove}
          deleteLabel="Delete note"
        />
      )}
    </div>
  )
}

const EVENT_META: Record<string, { icon: typeof Info; tone: string }> = {
  created: { icon: Plus, tone: 'bg-sky-500/10 text-sky-600 dark:text-sky-400' },
  moved: { icon: ArrowRightLeft, tone: 'bg-brand-400/10 text-brand-600 dark:text-brand-400' },
  updated: { icon: Pencil, tone: 'bg-gray-500/10 text-gray-600 dark:text-gray-300' },
  task: { icon: ListChecks, tone: 'bg-green-500/10 text-green-600 dark:text-green-400' },
  log: { icon: MessageSquare, tone: 'bg-violet-500/10 text-violet-600 dark:text-violet-400' },
}

function HistoryTab({ cardId }: { cardId: string }) {
  const [events, setEvents] = useState<CardEvent[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchCardEvents(cardId).then(setEvents).catch(e => setError(e?.message || 'Could not load history.'))
  }, [cardId])

  const days = useMemo(() => groupByDay(events ?? []), [events])

  if (error) return <p className="text-sm text-red-500">{error}</p>
  if (!events) return <Spinner />
  if (events.length === 0) return <Empty text="No history yet." />

  return (
    <div className="space-y-5">
      {days.map(day => (
        <div key={day.key}>
          <h3 className={`${sectionTitleClass} mb-2.5`}>{day.label}</h3>
          <ol>
            {day.items.map((ev, i) => {
              const meta = EVENT_META[ev.kind] ?? EVENT_META.updated
              const Icon = meta.icon
              const last = i === day.items.length - 1
              return (
                <li key={ev.id} className={`relative flex gap-3 ${last ? '' : 'pb-3.5'}`}>
                  {!last && <span className="absolute left-3.5 top-8 bottom-0 w-px bg-gray-200 dark:bg-white/10" aria-hidden />}
                  <div className={`h-7 w-7 flex-shrink-0 rounded-full flex items-center justify-center ${meta.tone}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex-1 min-w-0 pt-0.5">
                    <p className="text-sm text-gray-900 dark:text-gray-100 break-words">{ev.summary}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      <time dateTime={ev.createdAt} className="tabular-nums">{format(parseISO(ev.createdAt), 'h:mm a')}</time>
                      {ev.actorEmail ? ` · ${ev.actorEmail}` : ''}
                    </p>
                  </div>
                </li>
              )
            })}
          </ol>
        </div>
      ))}
    </div>
  )
}

function groupByDay<T extends { createdAt: string }>(items: T[]): { key: string; label: string; items: T[] }[] {
  const groups: { key: string; label: string; items: T[] }[] = []
  for (const item of items) {
    const d = parseISO(item.createdAt)
    const key = format(d, 'yyyy-MM-dd')
    let group = groups[groups.length - 1]
    if (!group || group.key !== key) {
      const label = isToday(d) ? 'Today' : isYesterday(d) ? 'Yesterday' : format(d, isThisYear(d) ? 'EEEE, MMM d' : 'EEE, MMM d, yyyy')
      group = { key, label, items: [] }
      groups.push(group)
    }
    group.items.push(item)
  }
  return groups
}

function TasksTab({
  card,
  staff,
  actorEmail,
  onChanged,
}: {
  card: BoardCard
  staff: StaffOption[]
  actorEmail: string | null
  onChanged: () => void
}) {
  const [tasks, setTasks] = useState<CardTask[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [title, setTitle] = useState('')
  const [assigneeId, setAssigneeId] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [adding, setAdding] = useState(false)
  const [showDone, setShowDone] = useState(false)
  const titleRef = useRef<HTMLInputElement>(null)

  const load = useCallback(() => {
    fetchCardTasks(card.id).then(setTasks).catch(e => setError(e?.message || 'Could not load tasks.'))
  }, [card.id])

  useEffect(load, [load])

  const staffName = useMemo(() => Object.fromEntries(staff.map(s => [s.id, s.name])), [staff])

  const { open, done } = useMemo(() => {
    const list = tasks ?? []
    return {
      open: list
        .filter(t => !t.done)
        .sort((a, b) => (a.dueDate ?? '9999-12-31').localeCompare(b.dueDate ?? '9999-12-31') || a.createdAt.localeCompare(b.createdAt)),
      done: list.filter(t => t.done).sort((a, b) => (b.doneAt ?? '').localeCompare(a.doneAt ?? '')),
    }
  }, [tasks])

  const total = open.length + done.length
  const overdueCount = open.filter(t => t.dueDate && differenceInCalendarDays(parseISO(t.dueDate), new Date()) < 0).length

  const add = async () => {
    if (!title.trim()) return
    setAdding(true)
    setError(null)
    try {
      const t = await createCardTask(card.id, { title: title.trim(), assigneeId: assigneeId || null, dueDate: dueDate || null })
      await addCardEvent(card.id, 'task', `Task added: ${t.title}`, actorEmail)
      setTasks(prev => [...(prev ?? []), t])
      setTitle('')
      setAssigneeId('')
      setDueDate('')
      onChanged()
      titleRef.current?.focus()
    } catch (e: any) {
      setError(e?.message || 'Could not add the task.')
    } finally {
      setAdding(false)
    }
  }

  const toggle = async (task: CardTask) => {
    const next = !task.done
    setTasks(prev => (prev ?? []).map(x => (x.id === task.id ? { ...x, done: next, doneAt: next ? new Date().toISOString() : null } : x)))
    let saved: CardTask
    try {
      saved = await setCardTaskDone(task.id, next)
    } catch (e: any) {
      setTasks(prev => (prev ?? []).map(x => (x.id === task.id ? task : x)))
      setError(e?.message || 'Could not update the task.')
      return
    }
    setTasks(prev => (prev ?? []).map(x => (x.id === saved.id ? saved : x)))
    onChanged()
    if (saved.done) {
      addCardEvent(card.id, 'task', `Task done: ${saved.title}`, actorEmail).catch(e => console.error('Could not record task event (non-fatal):', e))
    }
  }

  const remove = async (task: CardTask) => {
    try {
      await deleteCardTask(task.id)
      setTasks(prev => (prev ?? []).filter(x => x.id !== task.id))
      onChanged()
    } catch (e: any) {
      setError(e?.message || 'Could not delete the task.')
    }
  }

  return (
    <div className="space-y-5">
      <div className={composerClass}>
        <div className="flex items-center gap-2.5 px-3.5 pt-3">
          <Plus className="h-4 w-4 text-gray-400 flex-shrink-0" />
          <input
            ref={titleRef}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
            placeholder="Add a task, e.g. Call back about times"
            className="flex-1 min-w-0 bg-transparent text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 px-3 pt-2.5 pb-3">
          <div className={pillClass(!!assigneeId)}>
            {assigneeId ? <Avatar name={staffName[assigneeId] ?? '?'} /> : <UserRound className="h-3.5 w-3.5" />}
            <span className="truncate max-w-[9rem]">{assigneeId ? staffName[assigneeId] ?? 'Unknown' : 'Assignee'}</span>
            <ChevronDown className="h-3 w-3 opacity-60" />
            <select
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
              aria-label="Assignee"
              className="absolute inset-0 w-full opacity-0 cursor-pointer"
            >
              <option value="">No assignee</option>
              {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <DatePill
            value={dueDate}
            onChange={setDueDate}
            placeholder="Due date"
            display={dueDate ? dueInfo(dueDate, false).label : ''}
            icon={CalendarDays}
          />
          <button
            onClick={add}
            disabled={adding || !title.trim()}
            className="ml-auto inline-flex items-center h-8 px-3.5 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {adding ? 'Adding…' : 'Add task'}
          </button>
        </div>
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      {!tasks ? (
        <Spinner />
      ) : total === 0 ? (
        <div className="flex flex-col items-center text-center py-10">
          <div className="h-11 w-11 rounded-full bg-brand-400/10 text-brand-600 dark:text-brand-400 flex items-center justify-center mb-3">
            <ListChecks className="h-5 w-5" />
          </div>
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">No tasks yet</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Add follow-ups for this card above.</p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-1.5 rounded-full bg-gray-100 dark:bg-white/10 overflow-hidden">
              <div
                className="h-full rounded-full bg-green-500 transition-[width] duration-500 ease-out"
                style={{ width: `${(done.length / total) * 100}%` }}
              />
            </div>
            <span className="text-xs font-medium tabular-nums text-gray-500 dark:text-gray-400">
              {done.length}/{total} done
            </span>
            {overdueCount > 0 && <span className="text-xs font-medium text-red-500">{overdueCount} overdue</span>}
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
              To do <span className="font-normal">({open.length})</span>
            </h3>
            {open.length === 0 ? (
              <p className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" /> All caught up.
              </p>
            ) : (
              <ul className="space-y-0.5">
                {open.map(t => (
                  <TaskRow key={t.id} task={t} assigneeName={t.assigneeId ? staffName[t.assigneeId] ?? 'Unknown' : null} onToggle={toggle} onDelete={remove} />
                ))}
              </ul>
            )}
          </div>

          {done.length > 0 && (
            <div>
              <button
                onClick={() => setShowDone(s => !s)}
                className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                <ChevronRight className={`h-3.5 w-3.5 transition-transform ${showDone ? 'rotate-90' : ''}`} />
                Completed <span className="font-normal">({done.length})</span>
              </button>
              {showDone && (
                <ul className="space-y-0.5 mt-1.5">
                  {done.map(t => (
                    <TaskRow key={t.id} task={t} assigneeName={t.assigneeId ? staffName[t.assigneeId] ?? 'Unknown' : null} onToggle={toggle} onDelete={remove} />
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

function TaskRow({
  task,
  assigneeName,
  onToggle,
  onDelete,
}: {
  task: CardTask
  assigneeName: string | null
  onToggle: (task: CardTask) => void
  onDelete: (task: CardTask) => void
}) {
  const due = task.dueDate ? dueInfo(task.dueDate, task.done) : null

  return (
    <li className="group flex items-start gap-3 -mx-2.5 px-2.5 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors">
      <button
        onClick={() => onToggle(task)}
        role="checkbox"
        aria-checked={task.done}
        aria-label={task.done ? 'Mark as not done' : 'Mark as done'}
        className={`mt-0.5 h-[18px] w-[18px] flex-shrink-0 rounded-full border-2 flex items-center justify-center transition-colors ${
          task.done
            ? 'bg-green-500 border-green-500 text-white'
            : 'border-gray-300 dark:border-gray-600 text-transparent hover:border-green-500 hover:text-green-500'
        }`}
      >
        <Check className="h-3 w-3" strokeWidth={3} />
      </button>

      <div className="flex-1 min-w-0">
        <p className={`text-sm leading-5 break-words ${task.done ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'}`}>
          {task.title}
        </p>
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 mt-1">
          {assigneeName ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
              <Avatar name={assigneeName} /> {assigneeName}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
              <UserRound className="h-3.5 w-3.5" /> Unassigned
            </span>
          )}
          {due && (
            <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-md ${due.tone}`}>
              <CalendarDays className="h-3 w-3" /> {due.label}
            </span>
          )}
          {task.done && task.doneAt && (
            <span className="text-xs text-gray-400 dark:text-gray-500">Done {format(parseISO(task.doneAt), 'MMM d')}</span>
          )}
          {task.autoKey && (
            <span
              className="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-md bg-violet-500/10 text-violet-600 dark:text-violet-400"
              title="Created automatically"
            >
              <Bot className="h-3 w-3" /> Auto
            </span>
          )}
        </div>
      </div>

      <button
        onClick={() => onDelete(task)}
        title="Delete task"
        aria-label="Delete task"
        className="p-1 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-500/10 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 transition"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </li>
  )
}

function dueInfo(dueDate: string, done: boolean): { label: string; tone: string } {
  const date = parseISO(dueDate)
  const days = differenceInCalendarDays(date, new Date())
  const short = format(date, isThisYear(date) ? 'MMM d' : 'MMM d, yyyy')
  const neutral = 'bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-300'
  if (done) return { label: short, tone: neutral }
  if (days < 0) return { label: `Overdue · ${short}`, tone: 'bg-red-500/10 text-red-600 dark:text-red-400' }
  if (days === 0) return { label: 'Today', tone: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' }
  if (days === 1) return { label: 'Tomorrow', tone: 'bg-brand-400/10 text-brand-600 dark:text-brand-400' }
  return { label: short, tone: neutral }
}

const composerClass =
  'rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-0 shadow-sm transition focus-within:border-brand-400 focus-within:ring-2 focus-within:ring-brand-400/20'

function pillClass(active: boolean) {
  return `relative inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border text-xs font-medium transition-colors ${
    active
      ? 'border-brand-400/40 bg-brand-400/10 text-brand-600 dark:text-brand-400'
      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5'
  }`
}

function DatePill({
  value,
  onChange,
  placeholder,
  display,
  icon: Icon,
}: {
  value: string
  onChange: (value: string) => void
  placeholder: string
  display: string
  icon: typeof CalendarDays
}) {
  return (
    <div className={pillClass(!!value)}>
      <Icon className="h-3.5 w-3.5" />
      {value ? display : placeholder}
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => {
          try {
            e.currentTarget.showPicker()
          } catch {
          }
        }}
        aria-label={placeholder}
        className="absolute inset-0 w-full opacity-0 cursor-pointer"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label={`Clear ${placeholder.toLowerCase()}`}
          className="relative z-10 -mr-1 p-0.5 rounded-full hover:bg-brand-400/20"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  )
}

const CHANNEL_META: Record<CardLog['channel'], { icon: typeof Phone; text: string; bg: string }> = {
  call: { icon: Phone, text: 'text-brand-600 dark:text-brand-400', bg: 'bg-brand-400/10' },
  whatsapp: { icon: MessageCircle, text: 'text-green-600 dark:text-green-400', bg: 'bg-green-500/10' },
  text: { icon: Smartphone, text: 'text-sky-600 dark:text-sky-400', bg: 'bg-sky-500/10' },
  email: { icon: Mail, text: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500/10' },
  registered: { icon: UserCheck, text: 'text-teal-600 dark:text-teal-400', bg: 'bg-teal-500/10' },
}

// A logged channel this version of the app doesn't know yet (the database got a new one first).
const UNKNOWN_CHANNEL_META = { icon: MessageSquare, text: 'text-gray-600 dark:text-gray-300', bg: 'bg-gray-500/10' }

// Wraps on a phone, where the five channels don't fit on one line.
const segmentGroupClass = 'inline-flex flex-wrap p-0.5 rounded-lg bg-gray-100 dark:bg-white/5'

function segmentClass(active: boolean) {
  return `inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-xs font-medium transition-colors ${
    active
      ? 'bg-white dark:bg-surface-100 text-gray-900 dark:text-white shadow-sm'
      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
  }`
}

function LogTab({ card, actorEmail, onChanged }: { card: BoardCard; actorEmail: string | null; onChanged: () => void }) {
  const [logs, setLogs] = useState<CardLog[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    channel: 'call' as CardLog['channel'],
    direction: 'out' as CardLog['direction'],
    outcome: '',
    noAnswer: false,
    followUpDate: '',
    note: '',
  })
  const outcomeRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    fetchCardLogs(card.id).then(setLogs).catch(e => setError(e?.message || 'Could not load the log.'))
  }, [card.id])

  const days = useMemo(() => groupByDay(logs ?? []), [logs])

  const tries = (logs ?? []).filter(l => l.noAnswer).length

  // A registration comes to us: it's always incoming, and never a try with no answer.
  const registered = form.channel === 'registered'
  const noAnswer = form.noAnswer && !registered

  const add = async () => {
    const outcome = noAnswer ? form.outcome.trim() || 'No answer' : form.outcome.trim()
    if (!outcome) {
      setError('Write the outcome.')
      outcomeRef.current?.focus()
      return
    }
    setSaving(true)
    setError(null)
    try {
      const l = await createCardLog(card.id, {
        channel: form.channel,
        direction: registered ? 'in' : form.direction,
        outcome,
        noAnswer,
        followUpDate: form.followUpDate || null,
        note: form.note.trim() || null,
      })
      await addCardEvent(card.id, 'log', `${optionLabel(LOG_CHANNEL_OPTIONS, l.channel)} (${l.direction}): ${l.outcome}`, actorEmail)
      setLogs(prev => [l, ...(prev ?? [])])
      setForm(f => ({ ...f, outcome: '', noAnswer: false, followUpDate: '', note: '' }))
      onChanged()
    } catch (e: any) {
      setError(e?.message || 'Could not save the entry.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-5">
      <div className={composerClass}>
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 pt-3">
          <div className={segmentGroupClass} role="radiogroup" aria-label="Channel">
            {LOG_CHANNEL_OPTIONS.map(o => {
              const meta = CHANNEL_META[o.value as CardLog['channel']]
              const Icon = meta.icon
              const active = form.channel === o.value
              return (
                <button
                  key={o.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setForm(f => ({ ...f, channel: o.value as CardLog['channel'] }))}
                  className={segmentClass(active)}
                >
                  <Icon className={`h-3.5 w-3.5 ${active ? meta.text : ''}`} />
                  {o.label}
                </button>
              )
            })}
          </div>
          {!registered && (
            <div className={segmentGroupClass} role="radiogroup" aria-label="Direction">
              {([
                ['out', 'Outgoing', ArrowUpRight],
                ['in', 'Incoming', ArrowDownLeft],
              ] as const).map(([value, label, Icon]) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={form.direction === value}
                  onClick={() => setForm(f => ({ ...f, direction: value }))}
                  className={segmentClass(form.direction === value)}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="px-3.5 pt-3 space-y-1.5">
          <input
            ref={outcomeRef}
            value={form.outcome}
            onChange={(e) => setForm(f => ({ ...f, outcome: e.target.value }))}
            onKeyDown={(e) => e.key === 'Enter' && add()}
            placeholder={noAnswer ? 'No answer' : 'What happened? e.g. Spoke to mom, wants Tuesdays'}
            aria-label="Outcome"
            className="w-full bg-transparent text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none"
          />
          <input
            value={form.note}
            onChange={(e) => setForm(f => ({ ...f, note: e.target.value }))}
            onKeyDown={(e) => e.key === 'Enter' && add()}
            placeholder="Add a note (optional)"
            aria-label="Note"
            className="w-full bg-transparent text-xs text-gray-600 dark:text-gray-400 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 px-3 pt-2.5 pb-3">
          {!registered && (
            <button
              type="button"
              aria-pressed={form.noAnswer}
              onClick={() => setForm(f => ({ ...f, noAnswer: !f.noAnswer }))}
              title={`Counts toward the ${NO_REPLY_CLOSE_AFTER}-try limit`}
              className={
                form.noAnswer
                  ? 'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400 text-xs font-medium transition-colors'
                  : pillClass(false)
              }
            >
              <PhoneOff className="h-3.5 w-3.5" /> No answer
            </button>
          )}
          <DatePill
            value={form.followUpDate}
            onChange={(v) => setForm(f => ({ ...f, followUpDate: v }))}
            placeholder="Follow up"
            display={form.followUpDate ? followUpInfo(form.followUpDate).label : ''}
            icon={CalendarClock}
          />
          <button
            onClick={add}
            disabled={saving}
            className="ml-auto inline-flex items-center h-8 px-3.5 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Saving…' : 'Add to log'}
          </button>
        </div>
        {noAnswer && (
          <p className="px-3.5 pb-3 -mt-1 text-xs text-red-500">
            Counts toward the {NO_REPLY_CLOSE_AFTER}-try limit for closing with no reply.
          </p>
        )}
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      {!logs ? (
        <Spinner />
      ) : logs.length === 0 ? (
        <div className="flex flex-col items-center text-center py-10">
          <div className="h-11 w-11 rounded-full bg-brand-400/10 text-brand-600 dark:text-brand-400 flex items-center justify-center mb-3">
            <MessageSquare className="h-5 w-5" />
          </div>
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Nothing logged yet</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Record calls, WhatsApp messages, texts and emails above.</p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-gray-500 dark:text-gray-400">
            <span className="tabular-nums">
              {logs.length} {logs.length === 1 ? 'entry' : 'entries'}
            </span>
            {tries > 0 && (
              <span className="inline-flex items-center gap-2">
                No-answer tries
                <span className="flex gap-1">
                  {Array.from({ length: NO_REPLY_CLOSE_AFTER }, (_, i) => (
                    <span key={i} className={`h-1.5 w-4 rounded-full ${i < tries ? 'bg-red-500' : 'bg-gray-200 dark:bg-white/10'}`} />
                  ))}
                </span>
                <span className={`tabular-nums font-medium ${tries >= NO_REPLY_CLOSE_AFTER ? 'text-red-500' : ''}`}>
                  {tries}/{NO_REPLY_CLOSE_AFTER}
                </span>
              </span>
            )}
          </div>

          <div className="space-y-5">
            {days.map(day => (
              <div key={day.key}>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2.5">{day.label}</h3>
                <ol>
                  {day.items.map((l, i) => (
                    <LogEntry key={l.id} log={l} last={i === day.items.length - 1} />
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function LogEntry({ log, last }: { log: CardLog; last: boolean }) {
  const meta = CHANNEL_META[log.channel] ?? UNKNOWN_CHANNEL_META
  const Icon = log.noAnswer ? PhoneOff : meta.icon
  const DirIcon = log.direction === 'out' ? ArrowUpRight : ArrowDownLeft
  const followUp = log.followUpDate ? followUpInfo(log.followUpDate) : null
  const showOutcome = !(log.noAnswer && log.outcome === 'No answer')

  return (
    <li className={`relative flex gap-3 ${last ? '' : 'pb-4'}`}>
      {!last && <span className="absolute left-4 top-9 bottom-0 w-px bg-gray-200 dark:bg-gray-700" aria-hidden />}
      <div
        className={`h-8 w-8 flex-shrink-0 rounded-full flex items-center justify-center ${
          log.noAnswer ? 'bg-red-500/10 text-red-500' : `${meta.bg} ${meta.text}`
        }`}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0 pt-1">
        <div className="flex items-start justify-between gap-2">
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-medium text-gray-900 dark:text-gray-100">
            {optionLabel(LOG_CHANNEL_OPTIONS, log.channel)}
            <span className="inline-flex items-center gap-0.5 text-xs font-normal text-gray-500 dark:text-gray-400">
              <DirIcon className="h-3 w-3" /> {log.direction === 'out' ? 'Outgoing' : 'Incoming'}
            </span>
            {log.noAnswer && (
              <span className="text-[11px] font-medium px-1.5 py-0.5 rounded-md bg-red-500/10 text-red-600 dark:text-red-400">No answer</span>
            )}
          </p>
          <time dateTime={log.createdAt} className="flex-shrink-0 text-xs tabular-nums text-gray-500 dark:text-gray-400">
            {format(parseISO(log.createdAt), 'h:mm a')}
          </time>
        </div>
        {showOutcome && <p className="text-sm text-gray-700 dark:text-gray-300 mt-0.5 break-words">{log.outcome}</p>}
        {(log.message || log.media.length > 0) && (
          <details className="mt-1.5">
            <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer">
              Message
              {log.media.length > 0 && ` · ${log.media.length} ${log.media.length === 1 ? 'attachment' : 'attachments'}`}
            </summary>
            {log.message && (
              <pre className="mt-2 whitespace-pre-wrap break-words font-sans text-xs text-gray-700 dark:text-gray-300">{log.message}</pre>
            )}
            {log.media.length > 0 && (
              <ul className="mt-2 space-y-1">
                {log.media.map((m, i) => (
                  <li key={i} className="text-xs text-gray-500 dark:text-gray-400 break-all">
                    <a href={m.url} target="_blank" rel="noopener noreferrer" className="text-brand-600 dark:text-brand-400 underline underline-offset-2">
                      {m.url}
                    </a>
                    {m.type && ` (${m.type})`}
                  </li>
                ))}
              </ul>
            )}
          </details>
        )}
        {log.note && (
          <p className="mt-1.5 text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-white/[0.03] border-l-2 border-gray-200 dark:border-gray-700 rounded-r-md px-2.5 py-1.5 break-words">
            {log.note}
          </p>
        )}
        {followUp && (
          <span className={`mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-md ${followUp.tone}`}>
            <CalendarClock className="h-3 w-3" /> {followUp.label}
          </span>
        )}
      </div>
    </li>
  )
}

function followUpInfo(followUpDate: string): { label: string; tone: string } {
  const date = parseISO(followUpDate)
  const days = differenceInCalendarDays(date, new Date())
  const short = format(date, isThisYear(date) ? 'MMM d' : 'MMM d, yyyy')
  if (days === 0) return { label: 'Follow up today', tone: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' }
  if (days === 1) return { label: 'Follow up tomorrow', tone: 'bg-brand-400/10 text-brand-600 dark:text-brand-400' }
  if (days > 1) return { label: `Follow up ${short}`, tone: 'bg-brand-400/10 text-brand-600 dark:text-brand-400' }
  return { label: `Follow up ${short}`, tone: 'bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-300' }
}

function Spinner() {
  return (
    <div className="flex justify-center py-8">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600" />
    </div>
  )
}

function Empty({ text }: { text: string }) {
  return <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">{text}</p>
}
